import { CircularProgress, Modal, ModalClose, ModalDialog } from '@mui/joy';

import { useDeleteRepoMutation } from '@/hooks/useSwr';

export function DeletePromptModal({
  handle,
  isPublic,
  open,
  onClose,
  onSuccess,
}: {
  handle: string;
  isPublic: boolean;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const {
    trigger: deletePrompt,
    isMutating,
    error: deleteError,
  } = useDeleteRepoMutation(handle, { onSuccess: onSuccess });

  const onSubmit = async (e) => {
    e.preventDefault();
    const res = await deletePrompt({});
    if (res !== undefined) {
      onClose();
    }
  };

  const disabled = !!deleteError || isMutating || !handle;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog>
        <ModalClose size="lg" />
        <form
          onSubmit={onSubmit}
          className="flex w-[480px] flex-col gap-6 rounded-md p-6"
        >
          <div className="flex flex-col gap-4 text-center">
            <div className="flex flex-col gap-2 text-lg font-semibold text-error">
              <span>{`Are you sure you want to delete ${handle}?`}</span>
              <span className="text-sm text-ls-black">
                This action cannot be undone. All commits will be deleted.
              </span>
            </div>
            <div className="text-sm">
              Please note: any references to this prompt in your code will no
              longer work. You can also archive your prompt in the edit screen
              in case you want to keep it around.
            </div>
            {isPublic && (
              <div className="text-sm text-gray-800">
                This is a <b>public</b> prompt, so other people may have
                referenced this prompt in their own code.
              </div>
            )}
          </div>
          <button
            type="submit"
            disabled={disabled}
            className={`inline-flex h-10 w-full items-center justify-center gap-2 rounded-md border border-red-500 px-4 ${
              disabled
                ? 'cursor-not-allowed bg-tertiary'
                : 'bg-red-500 hover:bg-red-600'
            }`}
          >
            <div className="text-sm font-medium leading-tight text-white">
              {isMutating ? (
                <CircularProgress
                  color="neutral"
                  size="sm"
                  style={{ marginTop: '2px' }}
                />
              ) : (
                'Delete prompt'
              )}
            </div>
          </button>
        </form>
      </ModalDialog>
    </Modal>
  );
}
